import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const wildlifeModule: ChecklistModuleDefinition = {
  code: 'wildlife',
  name: 'Wildlife & Recreation',
  description: 'Game populations, hunting, fishing potential',
  categories: [
    {
      code: 'game_populations',
      name: 'Game Populations',
      items: [
        {
          code: 'deer_population',
          title: 'Deer population assessment',
          description: 'White-tail/mule deer presence',
          isGroupTask: false,
          dataFields: [
            { key: 'deer_present', label: 'Deer Present', type: 'boolean' },
            { key: 'population_density', label: 'Population Density', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'low', label: 'Low' },
            ]},
            { key: 'trophy_potential', label: 'Trophy Potential', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'average', label: 'Average' },
              { value: 'limited', label: 'Limited' },
            ]},
            { key: 'browse_condition', label: 'Browse Condition', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Excellent deer hunting' },
            { flag: 'green', condition: 'Good deer population' },
          ],
        },
        {
          code: 'turkey_population',
          title: 'Turkey population',
          description: 'Wild turkey presence',
          isGroupTask: false,
          dataFields: [
            { key: 'turkey_present', label: 'Turkey Present', type: 'boolean' },
            { key: 'population', label: 'Population', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'low', label: 'Low' },
            ]},
            { key: 'roost_sites', label: 'Roost Sites Present', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'green', condition: 'Good turkey population' },
          ],
        },
        {
          code: 'upland_birds',
          title: 'Upland game birds',
          description: 'Quail, pheasant, grouse',
          isGroupTask: false,
          dataFields: [
            { key: 'species_present', label: 'Species Present', type: 'array' },
            { key: 'habitat_quality', label: 'Habitat Quality', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
          ],
        },
        {
          code: 'waterfowl',
          title: 'Waterfowl potential',
          description: 'Duck and goose hunting',
          isGroupTask: false,
          dataFields: [
            { key: 'waterfowl_present', label: 'Waterfowl Present', type: 'boolean' },
            { key: 'species', label: 'Species', type: 'array' },
            { key: 'migration_corridor', label: 'In Migration Corridor', type: 'boolean' },
            { key: 'hunting_potential', label: 'Hunting Potential', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'limited', label: 'Limited' },
            ]},
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Excellent waterfowl hunting' },
          ],
        },
        {
          code: 'other_game',
          title: 'Other game species',
          description: 'Additional huntable species',
          isGroupTask: false,
          dataFields: [
            { key: 'species', label: 'Other Species Present', type: 'array' },
            { key: 'notes', label: 'Notes', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'habitat',
      name: 'Habitat Assessment',
      items: [
        {
          code: 'habitat_diversity',
          title: 'Habitat diversity',
          description: 'Variety of habitat types',
          isGroupTask: false,
          dataFields: [
            { key: 'habitat_types', label: 'Habitat Types', type: 'multiselect', options: [
              { value: 'mature_hardwood', label: 'Mature Hardwood' },
              { value: 'young_forest', label: 'Young Forest' },
              { value: 'pine', label: 'Pine Forest' },
              { value: 'mixed_forest', label: 'Mixed Forest' },
              { value: 'open_field', label: 'Open Field' },
              { value: 'native_grass', label: 'Native Grass' },
              { value: 'food_plot', label: 'Food Plots' },
              { value: 'wetland', label: 'Wetland' },
              { value: 'creek_bottom', label: 'Creek Bottom' },
              { value: 'brush', label: 'Brush/Thicket' },
            ]},
            { key: 'diversity_rating', label: 'Diversity Rating', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'poor', label: 'Poor - Monoculture' },
            ]},
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Excellent habitat diversity' },
          ],
        },
        {
          code: 'food_sources',
          title: 'Natural food sources',
          description: 'Mast, browse, food plots',
          isGroupTask: false,
          dataFields: [
            { key: 'mast_producing_trees', label: 'Mast Trees', type: 'array' },
            { key: 'food_plots_exist', label: 'Food Plots Exist', type: 'boolean' },
            { key: 'food_plot_acres', label: 'Food Plot Acres', type: 'number', unit: 'acres' },
            { key: 'food_availability', label: 'Food Availability', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
          ],
        },
        {
          code: 'cover_bedding',
          title: 'Cover and bedding areas',
          description: 'Shelter and bedding habitat',
          isGroupTask: false,
          dataFields: [
            { key: 'cover_quality', label: 'Cover Quality', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
            { key: 'bedding_areas', label: 'Bedding Areas Identified', type: 'number' },
            { key: 'thermal_cover', label: 'Thermal Cover Present', type: 'boolean' },
          ],
        },
        {
          code: 'water_wildlife',
          title: 'Water availability for wildlife',
          description: 'Year-round water access',
          isGroupTask: false,
          dataFields: [
            { key: 'water_sources', label: 'Water Sources', type: 'array' },
            { key: 'year_round_water', label: 'Year-Round Water', type: 'boolean' },
            { key: 'distribution', label: 'Distribution Across Property', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
          ],
        },
      ],
    },
    {
      code: 'hunting_infrastructure',
      name: 'Hunting Infrastructure',
      items: [
        {
          code: 'stand_sites',
          title: 'Stand/blind locations',
          description: 'Existing hunting infrastructure',
          isGroupTask: false,
          dataFields: [
            { key: 'stand_count', label: 'Number of Stands/Blinds', type: 'number' },
            { key: 'stand_types', label: 'Stand Types', type: 'array' },
            { key: 'stand_condition', label: 'Condition', type: 'string' },
            { key: 'additional_sites', label: 'Additional Site Potential', type: 'number' },
          ],
        },
        {
          code: 'shooting_lanes',
          title: 'Shooting lanes/clearings',
          description: 'Cleared shooting areas',
          isGroupTask: false,
          dataFields: [
            { key: 'lanes_exist', label: 'Shooting Lanes Exist', type: 'boolean' },
            { key: 'maintenance_needed', label: 'Maintenance Needed', type: 'boolean' },
          ],
        },
        {
          code: 'hunting_cabin',
          title: 'Hunting cabin/lodge',
          description: 'On-site hunting accommodations',
          isGroupTask: false,
          dataFields: [
            { key: 'cabin_exists', label: 'Cabin Exists', type: 'boolean' },
            { key: 'cabin_sqft', label: 'Square Feet', type: 'number', unit: 'sq ft' },
            { key: 'sleeps', label: 'Sleeps', type: 'number' },
            { key: 'condition', label: 'Condition', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'fishing',
      name: 'Fishing Potential',
      items: [
        {
          code: 'fishing_assessment',
          title: 'Fishing assessment',
          description: 'Overall fishing potential',
          isGroupTask: false,
          dataFields: [
            { key: 'fishing_available', label: 'Fishing Available', type: 'boolean' },
            { key: 'water_bodies', label: 'Water Bodies', type: 'array' },
            { key: 'fish_species', label: 'Fish Species', type: 'array' },
            { key: 'fishing_quality', label: 'Fishing Quality', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Excellent fishing' },
          ],
        },
      ],
    },
    {
      code: 'recreation',
      name: 'Other Recreation',
      items: [
        {
          code: 'trail_system',
          title: 'Trail system',
          description: 'Hiking, ATV, horse trails',
          isGroupTask: false,
          dataFields: [
            { key: 'trails_exist', label: 'Trails Exist', type: 'boolean' },
            { key: 'trail_miles', label: 'Miles of Trails', type: 'number', unit: 'miles' },
            { key: 'trail_types', label: 'Trail Types', type: 'array' },
            { key: 'condition', label: 'Condition', type: 'string' },
          ],
        },
        {
          code: 'camping_sites',
          title: 'Camping opportunities',
          description: 'Camping sites and potential',
          isGroupTask: false,
          dataFields: [
            { key: 'camping_sites', label: 'Camping Sites', type: 'number' },
            { key: 'amenities', label: 'Amenities', type: 'array' },
          ],
        },
        {
          code: 'recreational_value',
          title: 'Overall recreational value',
          description: 'Summary of recreational potential',
          isGroupTask: true,
          dataFields: [
            { key: 'overall_rating', label: 'Overall Rating', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'limited', label: 'Limited' },
            ]},
            { key: 'primary_uses', label: 'Primary Uses', type: 'array' },
            { key: 'improvement_potential', label: 'Improvement Potential', type: 'string' },
          ],
        },
      ],
    },
  ],
};

